import { Injectable } from '@angular/core';
import { HttpClient, HttpXsrfTokenExtractor } from '@angular/common/http';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AuthenticationService } from 'app/auth/service/authentication.service';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FilesValidatorsService } from 'app/main/shared/validators/files-validators';
import { AuthHelperService } from 'app/auth/helpers/auth-helper.service';
import { UserRegistrationValidators } from '../authentication/form-validatation';
import { IOnboardingSubmitResponse } from './store/onboarding/onboarding.responses.dto';

@Injectable({
  providedIn: 'root'
})
export class CustomerOnboardFlowService {
  private token: string = '';
  constructor(
    private http: HttpClient,
    private authenticationService: AuthenticationService,
    private filesValidators: FilesValidatorsService,
    private httpXsrfTokenExtractor: HttpXsrfTokenExtractor,
    private authHelperService: AuthHelperService,
  ) { }



  buildOnboardingForm(formBuilder: FormBuilder, isInvited: boolean): FormGroup {
    const userProfileControls: { [key: string]: any } = {
      jobTitle: ['', Validators.required],
      phone: ['', Validators.required],
      profileImage: [null,
        [
          this.filesValidators.fileTypeValidator(['image/jpeg', 'image/png', 'image/jpg']),
          this.filesValidators.maxFileSizeValidator(2 * 1024 * 1024) //2MB
        ]
      ],
      noMarketing: [false]
    };

    if (isInvited) {
      userProfileControls['firstName'] = ['', Validators.required];
      userProfileControls['lastName'] = ['', Validators.required];
      userProfileControls['password'] = ['', [Validators.required, UserRegistrationValidators.password]];
      userProfileControls['terms'] = [false, Validators.requiredTrue];
    } else {
      userProfileControls['companyName'] = ['', Validators.required];
    }

    return formBuilder.group({
      userProfile: formBuilder.group(userProfileControls),
      tellUseAboutYourself: formBuilder.group({
        workType: ['', Validators.required],
      }),
      howArePlannnigToUse: formBuilder.group({
        planningToUse: ['', Validators.required],
      }),
    });
  }

  //should be same as backend app/Enums/User/WorkTypeEnum.php
  fetchWorkTypeList() {
    return [
      { name: 'Sales' },
      { name: 'Finance' },
      { name: 'Procurement' },
      { name: 'Human Resources' },
      { name: 'Legal' },
      { name: 'Founder' },
      { name: 'CEO' },
      { name: 'Marketing' },
      { name: 'Security' },
      { name: 'Other' }
    ];
  }

  //should be same as backend app/Enums/User/JobTitleEnum.php
  fetchJobTitleList() {
    return [
      { name: 'C-Level' },
      { name: 'VP' },
      { name: 'Director' },
      { name: 'Manager' },
      { name: 'Specialist / Coordinator / Representative' },
      { name: 'Other' },
    ];
  }

  convertDataToFormData(data: any): FormData {
    const formData = new FormData();
    function appendIfNotEmpty(key: string, value: any) {
      if (value !== '' && value !== undefined && value !== null) {
        formData.append(key, value);
      }
    }
    appendIfNotEmpty('first_name', data?.firstName);
    appendIfNotEmpty('last_name', data?.lastName);
    appendIfNotEmpty('password', data?.password);
    appendIfNotEmpty('system_used_purpose', data?.planningToUse);
    appendIfNotEmpty('work_type', data?.workType);
    appendIfNotEmpty('job_title', data?.jobTitle);
    appendIfNotEmpty('company_name', data?.companyName);
    appendIfNotEmpty('phone_no', data?.phoneNumber);
    if (data?.terms !== null) {
      appendIfNotEmpty('terms', data?.terms ? '1' : '0');
    }

    if (data?.noMarketing !== null) {
      appendIfNotEmpty('no_marketing', data?.noMarketing ? '1' : '0');
    } else {
      appendIfNotEmpty('no_marketing', '1');
    }
    if (data?.profileImage instanceof File) {
      formData.append('profile_image', data.profileImage);
    }

    return formData;
  }


  saveInvitedOnboardingData(data: any, apiUrl: string): Observable<IOnboardingSubmitResponse> {

    function getRelativeApiUrl(fullUrl: string): string {
      try {
        const url = new URL(fullUrl);
        let path = url.pathname + url.search;
        path = path.replace(/^\/api\/?/, '');
        return path;
      } catch (e) {
        return fullUrl.replace(/^\/api\/?/, '');
      }
    }

    const relativeApiUrl = getRelativeApiUrl(apiUrl);
    const formData = this.convertDataToFormData(data);
    const token = this.authHelperService.getLocalDevToken();
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`; //TODO: temporary fix while BE will allow to connect from localhost
    } else {
      headers['X-XSRF-TOKEN'] = this.httpXsrfTokenExtractor.getToken() //Attach X-XSRF-TOKEN token manually since HttpBackend handler does not do it out of the box
    }

    return this.authenticationService.getCSRFCookie()
      .pipe(
        switchMap(() => this.http.post<IOnboardingSubmitResponse>(
          relativeApiUrl,
          formData,
          {
            headers: {
              'accept': `application/json`,
              ...headers
            }
          })));
  }

  saveOnboardingData(data: any, token: string): Observable<IOnboardingSubmitResponse> {
    this.token = token;
    const formData = this.convertDataToFormData(data);
    formData.append('token', this.token);
    const headers = {};
    headers['X-XSRF-TOKEN'] = this.httpXsrfTokenExtractor.getToken(); // Attach X-XSRF-TOKEN token manually

    return this.authenticationService.getCSRFCookie()
      .pipe(
        switchMap(() => this.http.post<IOnboardingSubmitResponse>(
          'onboarding',
          formData,
          {
            headers: {
              'accept': `application/json`,
              ...headers
            }
          }))
      );
  }

  fixPhoneNumber(phoneControl: AbstractControl): string | false {
    if (typeof phoneControl.value === 'string') {
      return phoneControl.value;
    }
    else if (typeof phoneControl.value === 'object' && phoneControl.value !== null) {
      if (phoneControl.value.e164Number) {
        return phoneControl.value.e164Number;
      }
    }
    return false;
  }

  sendTeamInvitation(emailList: string[], token: string): Observable<{ onboarding: string }> {
    return this.http.post<{ onboarding: string }>('onboarding/team-invitation', {
      team_invite_token: token,
      emailList: emailList,
    }
    )
  }

  skipTeamInvitation(): Observable<{ onboarding: string }> {
    return this.authenticationService.getCSRFCookie()
      .pipe(
        switchMap(() => this.http.post<{ onboarding: string }>('onboarding/skip-invitation', {}))
      )
  }

  checkToken(token: string) {
    this.token = token;
    return this.authenticationService.getCSRFCookie()
      .pipe(
        switchMap(() => this.http.post('onboarding/check-token', { token: token }))
      )
  }

  checkTeamInviteToken(token: string): Observable<{ first_name: string }> {
    return this.http.post<{ first_name: string }>('onboarding/check-invite-team-token', { team_invite_token: token });
  }
}